import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, MessageCircle, Loader2, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackLead } from "@/lib/pixel";
import { toast } from "sonner";

const EMPTY = {
  name: "", mobile: "", whatsapp: "", email: "", company_name: "", city: "", state: "",
  brand: "", model: "", machine_type: "", budget_min: "", budget_max: "", quantity: "1",
  urgency: "Just exploring", message: "", company: "",
};

export function EnquiryModal({ machine, open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setV = (k) => (v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim()) {
      toast.error("Name and mobile number are required");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        machine_id: machine.id,
        budget_min: form.budget_min ? Number(form.budget_min) : 0,
        budget_max: form.budget_max ? Number(form.budget_max) : 0,
        quantity: form.quantity ? Number(form.quantity) : 1,
      };
      const { data } = await api.post("/enquiries", payload);
      trackLead({ content_name: machine.name, machine_code: machine.machine_code });
      setDone(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send enquiry");
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setDone(null);
    setForm(EMPTY);
    onClose();
  };

  if (!machine) return null;
  const isSpare = machine.listing_type === "spare";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="enquiry-modal" className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        {done ? (
          <div className="py-6 text-center" data-testid="enquiry-success">
            <CheckCircle2 className="mx-auto mb-4 h-16 w-16 text-emerald-500" />
            <h3 className="font-display text-2xl font-extrabold text-slate-900">ENQUIRY RECEIVED!</h3>
            <p className="mt-2 text-slate-600">Your enquiry has been received.</p>
            <p className="text-slate-600">Our team will contact you shortly.</p>
            <p className="mt-3 font-mono text-sm text-slate-400">Enquiry ID: {done.enquiry_id}</p>
            {done.whatsapp_url && (
              <a
                href={done.whatsapp_url}
                target="_blank"
                rel="noreferrer"
                data-testid="continue-whatsapp-btn"
                className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-whatsapp px-5 py-3 text-sm font-bold text-white"
              >
                <MessageCircle size={16} /> CONTINUE ON WHATSAPP
              </a>
            )}
            <button
              data-testid="continue-browsing-btn"
              onClick={close}
              className="mt-3 block w-full rounded-full border border-slate-300 py-2.5 text-sm font-bold text-slate-700"
            >
              CONTINUE BROWSING
            </button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-xl">{isSpare ? "Request a quote" : "Enquire about this machine"}</DialogTitle>
              <DialogDescription>Share your details and requirement. Our team will connect you with the seller. Enquiries are free.</DialogDescription>
            </DialogHeader>
            <div className="rounded-lg bg-slate-50 p-3 text-sm">
              <p className="font-semibold text-slate-900">{machine.name}</p>
              <p className="font-mono text-xs text-slate-400">{machine.machine_code}</p>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <input tabIndex={-1} autoComplete="off" value={form.company} onChange={set("company")} name="company" className="hidden" />
              <div>
                <Label>Name *</Label>
                <Input data-testid="enquiry-name" value={form.name} onChange={set("name")} placeholder="Your name" required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Mobile *</Label>
                  <Input data-testid="enquiry-mobile" value={form.mobile} onChange={set("mobile")} placeholder="10-digit mobile" required />
                </div>
                <div>
                  <Label>WhatsApp</Label>
                  <Input data-testid="enquiry-whatsapp" value={form.whatsapp} onChange={set("whatsapp")} placeholder="Optional" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>City</Label>
                  <Input data-testid="enquiry-city" value={form.city} onChange={set("city")} />
                </div>
                <div>
                  <Label>State</Label>
                  <Input data-testid="enquiry-state" value={form.state} onChange={set("state")} />
                </div>
              </div>

              <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-3">
                <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Your requirement (optional)</p>
                {!isSpare && (
                  <div className="mb-3 grid grid-cols-2 gap-3">
                    <div>
                      <Label>Preferred brand</Label>
                      <Input data-testid="enquiry-brand" value={form.brand} onChange={set("brand")} placeholder={machine.brand || "Any"} />
                    </div>
                    <div>
                      <Label>New / Used</Label>
                      <Select value={form.machine_type} onValueChange={setV("machine_type")}>
                        <SelectTrigger data-testid="enquiry-machine-type"><SelectValue placeholder="Any" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any</SelectItem>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="used">Used</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Budget from (₹)</Label>
                    <Input data-testid="enquiry-budget-min" type="number" value={form.budget_min} onChange={set("budget_min")} placeholder="Min" />
                  </div>
                  <div>
                    <Label>Budget to (₹)</Label>
                    <Input data-testid="enquiry-budget-max" type="number" value={form.budget_max} onChange={set("budget_max")} placeholder="Max" />
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <Label>Quantity</Label>
                    <Input data-testid="enquiry-quantity" type="number" min="1" value={form.quantity} onChange={set("quantity")} />
                  </div>
                  <div>
                    <Label>Urgency</Label>
                    <Select value={form.urgency} onValueChange={setV("urgency")}>
                      <SelectTrigger data-testid="enquiry-urgency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Immediate">Immediate</SelectItem>
                        <SelectItem value="Within a month">Within a month</SelectItem>
                        <SelectItem value="Just exploring">Just exploring</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div>
                <Label>Message</Label>
                <Textarea data-testid="enquiry-message" value={form.message} onChange={set("message")} rows={2} placeholder="I am interested in this machine..." />
              </div>
              <button
                data-testid="enquiry-submit"
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-brand py-3 font-bold text-white disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send size={18} />}
                SEND ENQUIRY
              </button>
              <p className="text-center text-[11px] text-slate-400">Enquiries are free. No registration required. You can continue on WhatsApp after submitting.</p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
